import {
  Checkbox,
  DangerButton,
  FieldErrorAndDescription,
  FormInput,
  Modal,
  ModalSizeEnum,
  PrimaryButton,
  Tooltip,
} from '@systemeio/ui'
import { eventLocationLocoKeys } from 'modules/calendar/constants/calendar-loco'
import {
  LocationButton,
  LocationTypeIcon,
} from 'modules/calendar/events/components/location-type-fields'
import { EventLocationTypeEnum, locationFields } from 'modules/calendar/events/constants'
import { useCheckZoomIntegration } from 'modules/calendar/events/hooks/use-check-zoom-integration'
import {
  EventCreateFormInterface,
  EventEditFormInterface,
  EventLocationInterface,
} from 'modules/calendar/events/types/event-interface'
import {
  filterLocationBasedOnTempState,
  hasMeetingLink,
} from 'modules/calendar/events/utils/online-meeting-utils'
import { useCalendars } from 'modules/profile/integrations/google/hooks/use-calendars'
import React, { useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { isValidUrl } from 'shared/utils/is-valid-url'

interface LocationOnlineMeetingModal {
  locations: EventLocationInterface[]
  setValue: UseFormSetValue<EventCreateFormInterface | EventEditFormInterface>
  error?: string
}

export const LocationOnlineMeetingModal = ({
  locations,
  setValue,
  error,
}: LocationOnlineMeetingModal) => {
  const { t } = useLocoTranslation()

  const [tempState, setTempState] = useState<{
    types: EventLocationTypeEnum[]
    meetingLink?: string
  }>({ types: [] })

  const [opened, setOpened] = useState<boolean>(false)
  const [errors, setErrors] = useState({ meetingLink: '' })

  const { isZoomIntegrated, isLoading: isZoomIntegratedLoading } = useCheckZoomIntegration()
  const { hasGoogleCalendars, isLoading: isGoogleCalendarsLoading } = useCalendars()

  const isLoading = isGoogleCalendarsLoading || isZoomIntegratedLoading

  const initializeTempState = () => {
    const onlineMeetingLocations = locations.filter(loc => hasMeetingLink(loc.locationType))

    const types = onlineMeetingLocations.map(loc => loc.locationType)
    const externalLinkLocation = onlineMeetingLocations.find(
      loc => loc.locationType === EventLocationTypeEnum.ExternalLink,
    )

    setTempState({
      types,
      meetingLink: externalLinkLocation?.meetingLink || '',
    })
  }

  const deleteLocation = () => {
    setValue('locations', filterLocationBasedOnTempState(locations))
    setOpened(false)
  }

  const onSubmit = () => {
    if (tempState.types.length === 0) {
      setErrors(prev => ({
        ...prev,
        meetingLink: t('dashboard.validation.empty_error.title'),
      }))
      return
    }

    if (tempState.types.includes(EventLocationTypeEnum.ExternalLink) && !tempState.meetingLink) {
      setErrors(prev => ({
        ...prev,
        meetingLink: t('dashboard.validation.empty_error.title'),
      }))
      return
    }

    if (tempState.meetingLink && !isValidUrl(tempState.meetingLink)) {
      setErrors(prev => ({
        ...prev,
        meetingLink: t('dashboard.calendar.meeting_link_validation.title'),
      }))
      return
    }

    const newLocations = tempState.types.map(locationType => ({
      ...locationFields[locationType],
      locationType,
      ...(locationType === EventLocationTypeEnum.ExternalLink && {
        meetingLink: tempState.meetingLink,
      }),
    }))

    setValue('locations', [...filterLocationBasedOnTempState(locations), ...newLocations])
    setOpened(false)
  }

  return (
    <>
      <LocationButton
        locationType={EventLocationTypeEnum.ExternalLink}
        openLocationModal={() => {
          initializeTempState()
          setOpened(true)
        }}
        isFetching={isLoading}
        isSelected={locations.some(({ locationType }) => hasMeetingLink(locationType))}
      >
        <span className="flex flex-col items-center justify-center gap-3 ">
          <LocationTypeIcon locationType={EventLocationTypeEnum.ExternalLink} />
          {t(eventLocationLocoKeys[EventLocationTypeEnum.ExternalLink])}
        </span>
      </LocationButton>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={t(eventLocationLocoKeys[EventLocationTypeEnum.ExternalLink])}
        size={ModalSizeEnum.small}
        isFetching={isLoading}
        afterLeave={() => setTempState({ types: [] })}
      >
        <div className="flex flex-col gap-5">
          <Tooltip
            label={t('dashboard.calendar.event_inactive_zoom_location_error')}
            mode="hover"
            disabled={isZoomIntegrated}
          >
            <Checkbox
              key={`linkType-${EventLocationTypeEnum.Zoom}`}
              label={t('dashboard.calendar.event_location_type_zoom')}
              value={tempState.types.includes(EventLocationTypeEnum.Zoom)}
              disabled={!isZoomIntegrated}
              isFetching={isZoomIntegratedLoading}
              onChange={() =>
                setTempState(prev => ({
                  ...prev,
                  types: prev.types.includes(EventLocationTypeEnum.Zoom)
                    ? prev.types.filter(id => id !== EventLocationTypeEnum.Zoom)
                    : [...prev.types, EventLocationTypeEnum.Zoom],
                }))
              }
            />
          </Tooltip>

          <Tooltip
            label={t('dashboard.calendar.event_inactive_google_meet_location_error')}
            mode="hover"
            disabled={hasGoogleCalendars}
          >
            <Checkbox
              key={`linkType-${EventLocationTypeEnum.GoogleMeet}`}
              label={t('dashboard.calendar.event_location.google_meet')}
              value={tempState.types.includes(EventLocationTypeEnum.GoogleMeet)}
              disabled={!hasGoogleCalendars}
              isFetching={isGoogleCalendarsLoading}
              onChange={() =>
                setTempState(prev => ({
                  ...prev,
                  types: prev.types.includes(EventLocationTypeEnum.GoogleMeet)
                    ? prev.types.filter(id => id !== EventLocationTypeEnum.GoogleMeet)
                    : [...prev.types, EventLocationTypeEnum.GoogleMeet],
                }))
              }
            />
          </Tooltip>

          <Checkbox
            key={`linkType-${EventLocationTypeEnum.ExternalLink}`}
            label={t('dashboard.calendar.event_location_type_external_link')}
            value={tempState.types.includes(EventLocationTypeEnum.ExternalLink)}
            onChange={() =>
              setTempState(prev => ({
                ...prev,
                types: prev.types.includes(EventLocationTypeEnum.ExternalLink)
                  ? prev.types.filter(id => id !== EventLocationTypeEnum.ExternalLink)
                  : [...prev.types, EventLocationTypeEnum.ExternalLink],
              }))
            }
          />

          <FieldErrorAndDescription className="mt-2" error={error} />

          {tempState.types.includes(EventLocationTypeEnum.ExternalLink) && (
            <FormInput
              value={tempState.meetingLink || ''}
              onChange={e => {
                if (errors.meetingLink) setErrors(prev => ({ ...prev, meetingLink: '' }))
                setTempState(prev => ({ ...prev, meetingLink: e.target.value }))
              }}
              label={t('dashboard.calendar.event_location_meeting_link')}
              required
              error={errors.meetingLink}
            />
          )}
        </div>

        <div className="mt-6 flex gap-4 lg:mt-10">
          <DangerButton
            className="w-full"
            width="large"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              deleteLocation()
            }}
          >
            {t('global.delete')}
          </DangerButton>
          <PrimaryButton
            className="w-full"
            width="large"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              onSubmit()
            }}
          >
            {t('global.save')}
          </PrimaryButton>
        </div>
      </Modal>
    </>
  )
}
